// Generic OIDC configuration (provider-portable: Auth0 now, Keycloak later).
//
// The identity ADR requires that switching IdP is a *configuration* change, not a
// code change. The app must therefore never hardcode a vendor name ("auth0") in a
// path or identifier. All provider specifics are derived from these env values:
//
//   VITE_OIDC_ISSUER_URL   – the IdP issuer (discovery via /.well-known/openid-configuration)
//   VITE_OIDC_CLIENT_ID    – the public client id
//   VITE_OIDC_AUDIENCE     – the API audience
//   VITE_OIDC_PROVIDER_PATH– the path segment our BFF mounts the provider under
//                            (today the backend serves `/auth/auth0/*`; set this to
//                            `keycloak` or similar when the backend route changes).
//
// There are deliberately no AUTH0_* names and no @auth0/* imports here.

const env = (import.meta as unknown as { env?: Record<string, string> }).env ?? {};

/** Base URL of our backend (already includes `/api`). */
export const API_URL: string = env.VITE_API_URL ?? '';

/** Standard OIDC values, derived from discovery on the backend. */
export const OIDC_ISSUER_URL: string | undefined = env.VITE_OIDC_ISSUER_URL;
export const OIDC_CLIENT_ID: string | undefined = env.VITE_OIDC_CLIENT_ID;
export const OIDC_AUDIENCE: string | undefined = env.VITE_OIDC_AUDIENCE;

/**
 * The provider path segment our Backend-For-Frontend mounts the OIDC endpoints
 * under. Defaults to the current backend mount (`auth0`) so nothing breaks today;
 * moving to Keycloak is a single env change here (plus the matching backend route),
 * with no application code edits.
 */
export const OIDC_PROVIDER_PATH: string = env.VITE_OIDC_PROVIDER_PATH ?? 'auth0';

/** Build an absolute URL to one of our BFF OIDC endpoints, with optional query. */
function oidcUrl(action: 'login' | 'logout', params?: Record<string, string | undefined>): string {
  const base = `${API_URL}/auth/${OIDC_PROVIDER_PATH}/${action}`;
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v != null && v !== '') search.set(k, v);
  }
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

/** URL that starts an Authorization Code + PKCE login via our BFF. */
export function buildLoginUrl(params?: {
  role?: string;
  signup?: boolean;
  connection?: string;
  prompt?: string;
  acrValues?: string;
}): string {
  return oidcUrl('login', {
    role: params?.role,
    signup: params?.signup ? 'true' : undefined,
    connection: params?.connection,
    prompt: params?.prompt,
    acr_values: params?.acrValues,
  });
}

/** URL that ends the IdP session via our BFF. */
export function buildLogoutUrl(): string {
  return oidcUrl('logout');
}
